import React from "react";
import {
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Container,
  Button,
  Stack,
  Box
} from "@mui/material";
import { ReactComponent as QuantaIcon } from "assets/icons/quanta.svg";
import { ReactComponent as EngineeringForFreedomIcon } from "assets/icons/engineering-for-freedom.svg";
import { ReactComponent as HomeIcon } from "assets/icons/home.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { userInitials } from "utils/common";
import { ReactComponent as CogIcon } from "assets/icons/cog.svg";
import "./Header.scss";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useUserLogout,
  useUser,
  useUserProfileImage
} from "services/users.service";
import QueryStatus from "types/QueryStatus.enum";
import { useAppDispatch } from "context/hooks";
import { logout } from "context/actions";
import { setMachineSearchService } from "context/modal.context";
import RBACWrapper from "common/core/RBACWrapper";
import Permission from "types/Permission.enum";

function Header() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = instance.getActiveAccount();
  const { t } = useTranslation();
  const { profileImage } = useUserProfileImage(isAuthenticated);
  const { data: user, status } = useUser(isAuthenticated);
  const dispatch = useAppDispatch();
  const logoutAudit = useUserLogout(logOut);

  const name = user?.isActive
    ? `${user?.firstName} ${user?.lastName}`
    : activeAccount?.name;

  const handleOpenModal = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  function logOut() {
    dispatch(logout());
    instance.logoutRedirect({
      account: activeAccount
    });
  }

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleService = () => {
    dispatch(setMachineSearchService({ open: true }));
  };

  return (
    <Box className="header">
      <Container maxWidth="xl" classes={{ root: "header__container" }}>
        <div className="header__container__quanta-icon">
          <Link
            style={{ display: "flex" }}
            to="/"
            aria-label="Link to Homepage"
          >
            <QuantaIcon data-testid="quanta-icon" />
          </Link>
        </div>
        <Stack direction="row" className="header__container__background-box">
          <Stack direction="row" spacing={2} alignItems="center">
            <Link
              style={{ display: "flex" }}
              to="/"
              aria-label="Link to Homepage"
            >
              <HomeIcon data-testid="home-icon" />
            </Link>
            <EngineeringForFreedomIcon
              data-testid="engineering-for-freedom-icon"
              width={260}
            />
          </Stack>
          {isAuthenticated ? (
            <Stack direction="row" spacing={2} alignItems="center">
              {user?.isActive ? (
                <RBACWrapper
                  requiredPermissions={[Permission.ADD_UPDATE_MACHINE_SERVICE]}
                >
                  <Button
                    classes={{ root: "header__container__service-button" }}
                    variant="contained"
                    startIcon={<CogIcon />}
                    onClick={handleService}
                  >
                    <Typography fontWeight="medium">
                      {t("header.service")}
                    </Typography>
                  </Button>
                </RBACWrapper>
              ) : null}
              {status === QueryStatus.success ? (
                <Box
                  display="flex"
                  onClick={handleOpenModal}
                  sx={{ cursor: "pointer" }}
                >
                  <Avatar
                    src={profileImage}
                    alt={name}
                    classes={{ root: "header__container__profile" }}
                  >
                    {userInitials(name)}
                  </Avatar>
                  <Button
                    endIcon={<ExpandMoreIcon htmlColor="#00d3c9" />}
                    sx={{ display: { sm: "none", lg: "flex" } }}
                  >
                    <Typography
                      variant="body1"
                      color="#00d3c9"
                      textTransform="capitalize"
                    >
                      {name}
                    </Typography>
                  </Button>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
        {isAuthenticated ? (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{ margin: 1 }}
          >
            <MenuItem
              onClick={() => logoutAudit.mutate()}
              sx={{ width: { sm: 125, lg: 200 } }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("header.logout")}</ListItemText>
            </MenuItem>
          </Menu>
        ) : null}
      </Container>
    </Box>
  );
}

export default Header;
